/* eslint-disable no-restricted-syntax */
import React, { useEffect, useRef } from 'react';
import { useMediaQuery } from 'react-responsive';
import * as mobx from 'mobx';
import useResizeObserver from '@react-hook/resize-observer';
import { useLocation } from 'react-router-dom';
import { useParallaxController } from 'react-scroll-parallax';
import { WIDTH_THRESHOLD } from './constants';

let tryForeverSessions = {};
export const getNumberFromText = (text) => {
  return text?.replace(',', '')?.match(/\d+(\.\d+)?/g)?.[0];
};

export function numberInGroups(n) {
  if (!n) return '...';
  const s = n?.toString();
  if (s.includes('.')) {
    const [d, f] = s.split('.');
    const newD = d.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return `${newD}.${f}`;
  }
  return n?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') ?? 0;
}

export function decimalDisplay(n) {
  if (!n.includes('.') && n.length > 0) {
    return `${n}.00`;
  }
  return n;
}

export function getAccountDisplayNumber(s) {
  return decimalDisplay(numberInGroups(s));
}

export const sleep = (ms) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve();
    }, ms);
  });
};

export const tryForever = async (fn, id) => {
  let i = 0;
  let session = Math.random();
  tryForeverSessions[id] = session;
  // console.log('[tryForever] Start', id, session);

  // Using a variable condition instead of while(true)
  let shouldContinue = true;
  while(shouldContinue) {
    if (tryForeverSessions[id] !== session) {
      break;
    }
    try {
      // eslint-disable-next-line no-await-in-loop
      await fn();
      shouldContinue = false; // Exit the loop after successful execution
    } catch (error) {
      console.error('[tryForever] E', id, session, i, error);
    }
    if (shouldContinue) {
      // eslint-disable-next-line no-await-in-loop
      await sleep((++i ** 1.5) * 1000);
    }
  }
};


export function roundToDP(number, dp = 2) {
  const d = 10 ** dp;
  return Math.round(number * d) / d;
}

export const getPlaceHolderURL = (size) => {
  return `https://via.placeholder.com/${size}`;
};

export const useIsDesktop = () => {
  const isDesktopOrLaptop = useMediaQuery({
    query: `(min-width: ${WIDTH_THRESHOLD}px)`,
  });
  return isDesktopOrLaptop;
};


export function hexEncode(s: string) {
  let hex;
  let i;

  let result = '';
  for (i = 0; i < s.length; i++) {
    hex = s.charCodeAt(i).toString(16);
    if (hex.length === 1) {
      result += `0${hex}`;
    } else {
      result += `${hex}`;
    }
  }
  return result;
}

export const useSize = (target) => {
  const [size, setSize] = React.useState();

  React.useLayoutEffect(() => {
    if (target && target.current) {
      setSize(target.current.getBoundingClientRect());
    }
  }, [target]);

  // Where the magic happens
  useResizeObserver(target, entry => setSize(entry.contentRect));
  return size;
};

export const interpolateFn = ({
  inputRange,
  outputRange,
}: {
  inputRange: [number],
  outputRange: [number],
}) => {
  const il = inputRange.length;
  return (currentValue) => {
    // console.log('call interpolate');
    for (let i = 0; i < il; i++) {
      if (currentValue === inputRange[i]) return outputRange[i];
      if (currentValue < inputRange[i]) {
        if (i === 0) return outputRange[i];
        const lowerIn = Math.min(inputRange[i - 1], inputRange[i]);
        const upperIn = Math.max(inputRange[i - 1], inputRange[i]);
        const progress = Math.abs((currentValue - lowerIn) / (upperIn - lowerIn));
        return (outputRange[i] - outputRange[i - 1]) * progress + outputRange[i - 1];
      }
    }
    return outputRange[il - 1];
  };
};

export const genNumArray = (from, to) => {
  const arr = [];
  for (let i = from; i <= to; i++) {
    arr.push(i);
  }
  return arr;
};

export const simplePermissionString = (key) => {
  const arr = [];
  const splitted = key.split('/');
  arr.push(splitted.splice(-1, 1)[0]);
  splitted.forEach((str) => {
    arr.push(str.replace(/_/g, ' '));
  });
  return arr.join(' ');
};

export function getPermissionSpreadedKeys(permission) {
  if (!permission) return {};
  const keys = {};
  const runner = (tmpKeys: [], obj: {}) => {
    const entries = Object.entries(obj);
    for (let i = 0; i < entries.length; i++) {
      const [k, v] = entries[i];
      const tk = tmpKeys.concat(k);
      if (typeof v !== 'object') {
        keys[tk.join('/')] = v;
      } else {
        runner(tk, v);
      }
    }
  };
  runner([], permission);
  return keys;
}

export function getSpreadedToPermission(spreaded) {
  const obj = {};
  Object.entries(spreaded).forEach(([k, v]) => {
    let _obj = obj;
    const splitted = k.split('/');
    for (let i = 0; i < splitted.length; i++) {
      const kk = splitted[i];
      if (i === splitted.length - 1) {
        _obj[kk] = v;
      }
      if (!_obj[kk]) {
        _obj[kk] = {};
      }
      _obj = _obj[kk];
    }
  });
  return obj;
}

export const deepCopy = v => JSON.parse(JSON.stringify(v));

export function download(filename, text) {
  let element = document.createElement('a');
  element.setAttribute('href', `data:text/plain;charset=utf-8,${encodeURIComponent(text)}`);
  element.setAttribute('download', filename);

  element.style.display = 'none';
  document.body.appendChild(element);

  element.click();

  document.body.removeChild(element);
}

export function padDoubleQuote(str: String) {
  if (!str) return '';
  if (str.includes(',')) {
    return `"${str.replace(/"/gi, '”')}"`;
  }
  return str;
}

export function copyObj(obj) {
  return JSON.parse(JSON.stringify(obj));
}

export const getAccountLabel = (acc) => {
  return `${acc.code} - ${acc.name}`;
};

// eslint-disable-next-line symbol-description
const annotationsSymbol = Symbol();
const objectPrototype = Object.prototype;

export function makeSimpleAutoObservable(target, overrides, options) {
  // Make sure nobody called makeObservable/makeAutoObservable/extendObservable/makeSimpleAutoObservable previously (eg in parent constructor)
  if (mobx.isObservable(target)) {
    throw new Error('Target must not be observable');
  }

  let annotations = target[annotationsSymbol];
  if (!annotations) {
    annotations = {};
    const proto = Object.getPrototypeOf(target);
    let current = proto;
    const processTarget = (t) => {
      for (let prop in t) {
        if (Object.prototype.hasOwnProperty.call(t, prop)) {
          // eslint-disable-next-line no-nested-ternary
          annotations[prop] = !overrides ? true : prop in overrides ? overrides[prop] : true;
        }
      }
    };
    processTarget(target);
    while(current && current !== objectPrototype) {
      // processTarget(current);
      Reflect.ownKeys(current).forEach((key) => {
        if (key === mobx.$mobx || key === 'constructor') return;
        // eslint-disable-next-line no-nested-ternary
        annotations[key] = !overrides ? true : key in overrides ? overrides[key] : true;
      });
      current = Object.getPrototypeOf(current);
    }
    // Cache if class
    if (proto && proto !== objectPrototype) {
      Object.defineProperty(proto, annotationsSymbol, { value: annotations });
    }
  }
  return mobx.makeObservable(target, annotations, options);
}

export function formatAttributeText(c, data, attrKey, isShort) {
  const k = attrKey || c.attrKey;
  const sourceData = c.sourceData || c.dataColProps?.sourceData;
  const v = data.attributes[k];
  if (sourceData && sourceData[v]) {
    return isShort ? (sourceData[v].short || sourceData[v].label) : sourceData[v].label;
  }
  if (!v) {
    return '';
  }
  return typeof v === 'string' ? v : v.toFixed(2);
}

export function getListURLFromDetailPagePathname(pathname) {
  const splitted = pathname.split('/');
  splitted.splice(-1, 1);
  return splitted.join('/');
}

export const ig = (e) => {
  e.stopPropagation();
  window.open('https://www.instagram.com/daykarkal/', '_blank');
  return null;
};

export const fb = (e) => {
  e.stopPropagation();
  window.open('https://www.facebook.com/DayKarkal/', '_blank');
  return null;
};

export const whatsAppContact = (e) => {
  e.stopPropagation();
  window.open('https://bit.ly/3uoR8Jp', '_blank');
  return null;
};

const DIAMOND_SUPPLIER_MAPPER = [
  {org: 'CHINASTAR', c: 'CS'},
  {org: 'kapugems', c: 'KG'},
];

export const diamondIDToUIID = (id) => {
  if (typeof id !== 'string') return id;
  let finalId = id;
  DIAMOND_SUPPLIER_MAPPER.forEach(({org, c}) => {
    finalId = finalId.replace(org, c);
  });
  return finalId;
};

export const UIIDToDiamondID = (id:String) => {
  if (typeof id !== 'string') return id;
  let _id = id;
  DIAMOND_SUPPLIER_MAPPER.forEach(({org, c}) => {
    if (_id.includes(`${c}_`)) {
      _id = _id.replace(c, org);
    }
  });
  return _id;
};

export function useUpdateControllerOnRouteChange() {
  const location = useLocation();
  const parallaxController = useParallaxController();

  useEffect(() => {
    parallaxController.update();
  }, [location.pathname]);
}

export function linesToDiv(str:String, style) {
  if (typeof str !== 'string') return str;
  return str.split('\n').map((l) => {
    return (
      <div style={{style}}>{l}</div>
    );
  });
}

export function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value; // assign the value of ref to the argument
  }, [value]); // this code will run when the value of 'value' changes
  return ref.current; // in the end, return the current ref value.
}

export const isRealNumber = value => typeof value === 'number' && !Number.isNaN(value);

const thousandSeparatorRegExp = [/(\d)(?=(\d{3})+(?!\d))/g, '$1,'];

export const formatNumber = (value, decimalScale) => {
  const num = Number(value);
  if (num) {
    if (isRealNumber(decimalScale)) {
      const parts = num.toFixed(decimalScale).toString().split('.');
      parts[0] = parts[0].replace(...thousandSeparatorRegExp);
      return parts.join('.');
    }

    return isRealNumber(num)
      ? num.toString().replace(...thousandSeparatorRegExp)
      : '';
  }

  return value;
};

export const processAttr = (attr:String) => {
  if (typeof attr === 'string') {
    let newStr = attr;
    if (!attr.startsWith('http')) {
      newStr = newStr.replace('+', '');
      newStr = newStr.replace('-', '');
    }
    return newStr.toUpperCase();
  }
  return attr;
};
